import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-widest text-slate-600 font-medium">{label}</Label>
      {children}
    </div>
  );
}

export default function Customers() {
  const [rows, setRows] = useState([]);
  const empty = { name: "", contact_person: "", email: "", phone: "", address: "", gst_no: "" };
  const [form, setForm] = useState(empty);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => setRows((r => Array.isArray(r) ? r : [])((await api.get("/customers")).data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", form);
      setForm(empty);
      toast.success("Customer added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${confirmId}`);
      toast.success("Customer deleted");
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); setConfirmId(null); }
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-yellow-600 font-semibold mb-1">Master</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Customers</h1>
        <p className="text-slate-500 text-sm mt-2">Saved customers auto-fill when creating a new job card.</p>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 border-slate-200 lg:col-span-2">
          <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>All Customers</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Contact</th>
                  <th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Phone</th>
                  <th className="py-2 pr-3">GST</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-500">No customers yet</td></tr>}
                {rows.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium break-words">{c.name}</td>
                    <td className="py-2 pr-3 text-slate-600 break-words">{c.contact_person || "—"}</td>
                    <td className="py-2 pr-3 text-slate-600 text-xs break-words">{c.email || "—"}</td>
                    <td className="py-2 pr-3 text-slate-600 font-mono text-xs">{c.phone || "—"}</td>
                    <td className="py-2 pr-3 text-slate-600 font-mono text-xs">{c.gst_no || "—"}</td>
                    <td className="py-2 text-right"><Button variant="ghost" size="icon" onClick={() => setConfirmId(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Add Customer</div>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Name *"><Input maxLength={150} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
            <Field label="Contact Person"><Input maxLength={150} value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
            <Field label="Email"><Input type="email" maxLength={150} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Phone"><Input maxLength={150} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="GST No."><Input maxLength={150} value={form.gst_no} onChange={(e) => setForm({ ...form, gst_no: e.target.value })} /></Field>
            <Field label="Address"><Textarea rows={2} maxLength={500} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800"><Plus className="w-4 h-4 mr-2" /> Add</Button>
          </form>
        </Card>
      </div>
      <ConfirmDialog
        open={!!confirmId}
        title="Delete Customer?"
        description="This will permanently delete the customer. This cannot be undone."
        confirmLabel="Delete Customer"
        busy={deleting}
        onCancel={() => setConfirmId(null)}
        onConfirm={remove}
      />
    </Layout>
  );
}
